import { Alert, Button, Skeleton } from "@mui/material";
import { displayNotification } from "helpers";
import FormContext from "modules/pvs_activities/components/contexts/FormContext";
import FormEngine from "modules/pvs_activities/components/FormEngine.view";
import PopupModal from "modules/pvs_activities/components/PopupModal";
import useFormDataLoad from "modules/pvs_activities/hooks/form/useFormDataLoad";
import { useSubmitDFormMutation } from "modules/pvs_activities/redux/service/dForm";
import { useLazyGetSingleFormQuery } from "modules/pvs_activities/redux/service/forms";
import { func, oneOf, string } from "prop-types";
import React, { useMemo, useRef, useState } from "react";
import { FaSave } from "react-icons/fa";
import { useSelector } from "react-redux";

const defaultProps = {
  mode: "preview",
  id: "",
  movementId: "",
  children: () => {},
  onClose: () => {},
};

const PopupForm = (rawProps) => {
  const { children, mode, id, movementId, onClose } = {
    ...defaultProps,
    ...rawProps,
  };

  const modalRef = useRef();
  const footerTrigger = useRef();
  const authData = useSelector((s) => s.auth.data);

  const [sectionId, setSectionId] = useState("");
  const [formTrigger, formRes] = useLazyGetSingleFormQuery();
  const [submitDForm] = useSubmitDFormMutation();

  useFormDataLoad(
    formRes,
    sectionId,
    () => {
      setSectionId(formRes.data.first_section_id);
    },
    () => {
      modalRef.current?.closeModal();
    }
  );

  const handleSubmit = () => {
    if (
      movementId &&
      window.confirm("You are about to submit. Are you sure?")
    ) {
      const typ = "";
      submitDForm({
        templateId: id,
        movementId,
        typ,
        isValidated: "",
        userId: authData.id,
      }).then((response) => {
        if (response.error) {
          displayNotification("error", "An error occured when submitting");
        } else {
          displayNotification("success", response.data.message);
          modalRef.current?.closeModal();
        }
      });
    }
  };

  const handleSubmitwithoutAlert = () => {
    const typ = "";
    submitDForm({
      templateId: id,
      movementId,
      typ,
      isValidated: "",
      userId: authData.id,
    }).then((response) => {
      if (response.error) {
        displayNotification("error", "An error occured when submitting");
      } else {
        displayNotification("success", response.data.message);
        modalRef.current?.closeModal();
      }
    });
  };

  const isReadOnly = useMemo(() => {
    if (formRes.isSuccess) {
      if (mode === "data_entry") {
        if ([2, 6].includes(formRes.data?.movement?.status ?? 1)) return true;
        if (String(formRes.data?.movement?.user_id) !== String(authData.id))
          return true;
        return false;
      }
    }
    return true;
  }, [formRes, mode, authData]);

  return (
    <PopupModal
      ref={modalRef}
      title={
        formRes.isSuccess && sectionId !== ""
          ? formRes.data.name
          : formRes.isLoading || formRes.status === "uninitialised"
          ? "Loading Popup Form..."
          : formRes.isError
          ? "Error Loading Popup Form..."
          : "Popup Form"
      }
      triggers={({ openModal }) =>
        children &&
        children(() => {
          formTrigger(mode === "data_entry" ? { id, movementId } : { id });
          openModal();
        })
      }
      footTriggers={() =>
        mode === "data_entry" &&
        formRes.isSuccess &&
        sectionId !== "" &&
        formRes.data?.movement?.status === 1 &&
        isReadOnly === false ? (
          <>
            {formRes.data?.can_save === true && (
              <Button
                variant="contained"
                color="black"
                onClick={() => modalRef.current?.closeModal()}
                className="ms-2 my-auto h-100"
              >
                <FaSave className="me-1" />
                <span>Save & Close</span>
              </Button>
            )}
            {formRes.data?.can_submit === true && (
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  formRes.data?.submit_btn_text.toLowerCase() === "mark as completed"
                    ? handleSubmitwithoutAlert()
                    : handleSubmit()
                }
                className="ms-2 my-auto h-100"
              >
                <FaSave className="me-1" />
                <span>{formRes.data?.submit_btn_text ?? "Submit"}</span>
              </Button>
            )}
          </>
        ) : null
      }
      size="xl"
      onClose={() => onClose && onClose()}
    >
      <>
        {formRes.isSuccess && sectionId !== "" && modalRef.current?.isOpen ? (
          <FormContext
            mode={mode}
            templateId={id}
            sectionId={sectionId}
            movementId={movementId}
            missionId={formRes.data?.mission_id ? formRes.data?.mission_id : ""}
            movementStatus={formRes.data?.movement?.status ?? 1}
            formType={formRes.data.type_ref}
          >
            {["FT_FRM", "FT_RPT"].includes(formRes.data.type_ref) && (
              <FormEngine
                form={formRes.data}
                templateId={id}
                sectionId={sectionId}
                renderHeader={(output) => (footerTrigger.current = output())}
                isPopupForm={true}
                onSubmit={handleSubmit}
              />
            )}
            {["FT_LTR", "FT_EML"].includes(formRes.data.type_ref) && (
              <Alert severity="info">
                This Form Type is not supported in popup form.
              </Alert>
            )}
          </FormContext>
        ) : formRes.isLoading || formRes.status === "uninitialised" ? (
          <>
            <Skeleton
              variant="rounded"
              width={"100%"}
              height={150}
              sx={{ mb: 1 }}
            />
          </>
        ) : (
          ""
        )}
      </>
    </PopupModal>
  );
};

PopupForm.propTypes = {
  mode: oneOf(["preview", "data_entry"]),
  id: string,
  movementId: string,
  children: func,
  onClose: func,
};

export default PopupForm;
